// What the Cash page asks somebody to DO — pure functions over rows, in their own
// file for the reason `cash.custody.ts` gives (the hooks file cannot load under
// the admin vitest setup).

import { isOutstandingCash } from "./cash.custody";

/**
 * Midnight in Manila, `days - 1` days ago — the start of the History range.
 *
 * ⚠ MANILA, NOT THE BROWSER. `setHours(0)` used the viewer's own timezone, so an
 * office laptop set to anything but +08:00 drew the boundary somewhere else.
 * Manila has no daylight saving, so stepping back whole UTC days is exact.
 */
export function manilaRangeStart(days: number, now: Date = new Date()): string {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila" }).format(now);
  const start = new Date(`${today}T00:00:00+08:00`);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return start.toISOString();
}

/**
 * Work that is still waiting on somebody, whatever its age.
 *
 * ⚠ THE DATE RANGE IS FOR HISTORY ONLY. Filtering open work by `created_at`
 * made cash still owed on day 8 vanish from every queue and total until someone
 * widened the range — the money did not go anywhere, the screen just stopped
 * showing it.
 */
export function isOpenRemittance(row: { status: string | null; amount: number }): boolean {
  return isOutstandingCash(row) || row.status === "received";
}

/**
 * A handoff that still needs a confirmation.
 *
 * `issue_reported` is CLOSED: the recipient refused it, the payer still holds
 * the cash, and the process continues on a NEW handoff (0184/0187). Counting it
 * as "in progress" kept it on the badge for ever.
 */
export function isOpenHandoff(status: string): boolean {
  return status === "awaiting_payment" || status === "pending_confirmation";
}

/**
 * Can the Hub confirm this remittance directly?
 *
 * Only cash a Therapist handed STRAIGHT to the Hub (0166 instructs `hub` when her
 * pickup has no Rider) and that has not entered the handoff chain — the chain's
 * own Hub leg is the receipt for everything else. Mirrors `cash.service.ts`
 * `receive`, so the button is absent rather than certain to be refused.
 *
 * - `confirm` — handed over, the hub manager may count it and confirm
 * - `waiting` — instructed to the Hub, but she has not tapped hand-over yet
 * - `null`    — not the Hub's to confirm here
 */
export function hubReceiptState(
  row: {
    id: string;
    status: string | null;
    amount: number;
    hub_id: string | null;
    recipient_kind: string | null;
    transferred_at: string | null;
  },
  hubId: string | null,
  inHandoffChain: ReadonlySet<string>,
): "confirm" | "waiting" | null {
  if (!hubId || row.hub_id !== hubId) return null;
  if (!isOutstandingCash(row)) return null;
  if (row.recipient_kind !== "hub") return null;
  if (inHandoffChain.has(row.id)) return null;
  return row.transferred_at ? "confirm" : "waiting";
}
